import { Button, classesBouton, Dialog, Input } from '@artosera/ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router';
import { authApi } from '../../api/auth.js';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import type { SessionUtilisateur } from '../../api/types.js';
import { cheminDoubleAuthentification } from '../../auth/confinement.js';
import { CLE_SESSION } from '../../auth/queryClient.js';
import { lireSessionVerifiee } from '../../auth/session.js';
import { Field } from '../Field.js';
import styles from './StepUpDialog.module.css';

export interface StepUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Appelé après une vérification acceptée : au tour de l'appelant de rejouer son geste. */
  onVerifie: () => void;
  /** Raison affichée ; par défaut, celle du coffre confidentiel. */
  description?: string;
}

/**
 * Élévation du second facteur (§ 3, `POST /v1/auth/2fa/verify`), avant
 * d'ouvrir, modifier, télécharger ou archiver un document confidentiel du
 * coffre (§ 18.3). La réponse `403 acces.refuse`, `details.raison:
 * mfa_step_up_requis` (repérée par `estErreurStepUpRequis`, `api/coffre.ts`)
 * ouvre ce dialogue ; une vérification acceptée referme et rejoue le geste
 * d'origine — jamais une nouvelle tentative silencieuse.
 *
 * Un code de secours remplace le code de l'application, comme à la connexion.
 *
 * Un compte sans second facteur actif (en grâce, la double authentification
 * pas encore activée) reçoit le même refus, mais n'a aucun code à donner : le
 * dialogue l'envoie l'activer, dans Réglages › Sécurité de la galerie
 * courante — hors galerie (archive), sur `/app/double-authentification`.
 */
export function StepUpDialog({
  open,
  onOpenChange,
  onVerifie,
  description,
}: StepUpDialogProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [code, setCode] = useState('');
  // Un code de secours à la place du code de l'application (comme la connexion).
  const [secours, setSecours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Fermer la boîte rend caduque la vérification en vol : son succès tardif
  // ne rejoue pas le geste que la personne vient d'annuler.
  const tentative = useRef(0);
  // La session affichée, lue sans requête : posée par le chargeur de la page.
  const { data: session } = useQuery<SessionUtilisateur>({
    queryKey: CLE_SESSION,
    queryFn: lireSessionVerifiee,
    enabled: false,
    staleTime: Infinity,
  });
  const { slug } = useParams();
  const { pathname, search } = useLocation();
  const sansSecondFacteur = session?.compte.deuxiemeFacteurActive === false;
  const versSecurite = slug
    ? `/${slug}/reglages/securite`
    : cheminDoubleAuthentification(`${pathname}${search}`);

  function fermer(): void {
    tentative.current += 1;
    setCode('');
    setSecours(false);
    setErreur(null);
    setEnCours(false);
    onOpenChange(false);
  }

  async function verifier(): Promise<void> {
    const numero = ++tentative.current;
    setErreur(null);
    setEnCours(true);
    try {
      const saisi = code.trim();
      await authApi.mfaVerifier(secours ? { codeSecours: saisi } : { code: saisi });
      if (numero !== tentative.current) return;
      fermer();
      onVerifie();
    } catch (error) {
      if (numero !== tentative.current) return;
      if (estApiError(error)) setErreur(error.message);
      else if (estErreurReseau(error)) setErreur(t('commun:erreurReseau'));
      else setErreur(t('commun:erreurReseau'));
      setEnCours(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(ouvert) => {
        if (ouvert) onOpenChange(true);
        else fermer();
      }}
      title={t('regie:coffre.stepUp.titre')}
      description={
        sansSecondFacteur
          ? t('regie:coffre.stepUp.aActiver')
          : (description ?? t('regie:coffre.stepUp.description'))
      }
      closeLabel={t('commun:actions.fermer')}
      footer={
        sansSecondFacteur ? (
          <>
            <Button variant="ghost" onClick={fermer}>
              {t('commun:actions.annuler')}
            </Button>
            <Link className={classesBouton('primary')} to={versSecurite} onClick={fermer}>
              {t('regie:coffre.stepUp.activer')}
            </Link>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={fermer}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void verifier()}
              loading={enCours}
              disabled={code.trim().length === 0}
            >
              {t('regie:coffre.stepUp.verifier')}
            </Button>
          </>
        )
      }
    >
      {sansSecondFacteur ? null : (
        <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
          <Field
            label={
              secours ? t('regie:coffre.stepUp.champSecours') : t('regie:coffre.stepUp.champCode')
            }
            htmlFor="coffre-stepup-code"
          >
            <Input
              id="coffre-stepup-code"
              inputMode={secours ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              mono
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
          <Button
            variant="ghost"
            onClick={() => {
              setSecours((courant) => !courant);
              setCode('');
              setErreur(null);
            }}
          >
            {secours
              ? t('regie:coffre.stepUp.utiliserCode')
              : t('regie:coffre.stepUp.utiliserSecours')}
          </Button>
        </form>
      )}
    </Dialog>
  );
}
